<script setup lang="ts">
import {
  LucideUser,
  LucideLogOut,
  LucideHome,
  LucideBookmark,
  LucideBriefcase,
  LucideFileText
} from 'lucide-vue-next'
import { Button } from '~/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '~/components/ui/dropdown-menu'
import { useUser } from '~/composables/useUser'
import { useAuth } from '~/composables/useAuth'
const { getPathWithLocale } = useLocalizedPath()
const { user, isLoggedIn, userName, userInitials, hasPermission } = useUser()
const { logout } = useAuth()
const router = useRouter()

const canViewB2bLeads = computed(() =>
  hasPermission('b2b.leads.viewAny') || hasPermission('b2b.leads.own') || hasPermission('b2b.leads.common')
)

const handleLogout = async () => {
  await logout()
}

const navigateTo = (path: string) => () => router.push(getPathWithLocale(path))

const goToPanel = navigateTo('/panel')
const goToFavorites = navigateTo('/panel/favorites')
const goToB2bLeads = navigateTo('/b2b/leads')
const goToProfile = navigateTo('/panel/profile')
const goToProfileLeads = navigateTo('/profile/leads')

const { openLogin } = useAuthModal()

const handleAuthClick = () => {
  openLogin()
}
</script>

<template>
  <div>
    <!-- Open login modal on click -->
    <Button
      v-if="!isLoggedIn"
      variant="gray"
      size="icon"
      :title="$t('common.sign_in')"
      :aria-label="$t('common.sign_in')"
      @click="handleAuthClick"
    >
      <LucideUser class="size-5" />
    </Button>

    <!-- Authenticated state -->
    <DropdownMenu v-else :modal="false">
      <DropdownMenuTrigger as-child>
        <Button
          variant="gray"
          size="icon"
          :title="$t('widget.layout_header.user_auth.profile')"
          :aria-label="$t('widget.layout_header.user_auth.profile')"
          class="relative rounded-full"
        >
          <div
            v-if="user?.avatar"
            class="h-full w-full overflow-hidden rounded-lg transition-all duration-200 hover:scale-105 hover:shadow-md"
          >
            <NuxtImg
              :src="user.avatar"
              :alt="`${userName} avatar`"
              class="h-full w-full object-cover"
            />
          </div>
          <div
            v-else
            class="flex h-full w-full items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-blue-600 text-sm font-semibold text-white transition-all duration-200 hover:scale-105 hover:from-blue-600 hover:to-blue-700 hover:shadow-md"
          >
            {{ userInitials }}
          </div>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" class="w-56">
        <div class="px-2 py-1.5">
          <p class="truncate text-sm text-gray-500">{{ user?.email }}</p>
        </div>

        <DropdownMenuSeparator />

        <DropdownMenuItem class="cursor-pointer" @click="goToPanel">
          <LucideHome class="mr-2 h-4 w-4" />
          {{ $t('widget.layout_header.user_auth.panel') }}
        </DropdownMenuItem>

        <DropdownMenuItem class="cursor-pointer" @click="goToFavorites">
          <LucideBookmark class="mr-2 h-4 w-4" />
          {{ $t('widget.layout_header.user_auth.favorites') }}
        </DropdownMenuItem>

        <DropdownMenuItem class="cursor-pointer" @click="goToProfileLeads">
          <LucideFileText class="mr-2 h-4 w-4" />
          {{ $t('page.b2b.leads.filter.own') }}
        </DropdownMenuItem>

        <DropdownMenuItem v-if="canViewB2bLeads" class="cursor-pointer" @click="goToB2bLeads">
          <LucideBriefcase class="mr-2 h-4 w-4" />
          {{ $t('widget.layout_header.user_auth.b2b_leads') }}
        </DropdownMenuItem>

        <DropdownMenuItem class="cursor-pointer" @click="goToProfile">
          <LucideUser class="mr-2 h-4 w-4" />
          {{ userName }}
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          class="cursor-pointer text-red-600 focus:text-red-600"
          @click="handleLogout"
        >
          <LucideLogOut class="mr-2 h-4 w-4" />
          {{ $t('widget.layout_header.user_auth.logout') }}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
</template>
